import React, { useState, useEffect, useCallback } from 'react';
import { Card, Radio, Button, Progress, Typography, Space, Modal, message } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Title, Text } = Typography;
const { Group } = Radio;

const ExamPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { examId, questions, category } = location.state || {};
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(60 * 60); // 60分钟考试时间
  const [submitting, setSubmitting] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [whiteModalVisible, setWhiteModalVisible] = useState(false);

  // 如果没有试题数据，重定向到主页
  useEffect(() => {
    if (!examId || !questions || questions.length === 0) {
      message.error('试题数据加载失败');
      navigate('/');
    }
  }, [examId, questions, navigate]);

  // 提交答案到服务器
  const submitAnswers = async () => {
    try {
      setSubmitting(true);
      
      // 构造提交数据
      const answersArray = Object.entries(answers).map(([questionId, userAnswer]) => ({
        questionId,
        userAnswer
      }));
      
      const res = await axios.post('/api/exams/submit', {
        examId,
        answers: answersArray
      });
      
      console.log('提交成功，返回结果:', res.data);
      
      // 简单导航到结果页面，不传递状态
      message.success('考试提交成功');
      
      // 短暂延迟以确保服务器处理完成
      setTimeout(() => {
        navigate('/result');
      }, 500);
    } catch (err) {
      console.error('提交答案失败:', err);
      message.error('提交答案失败: ' + (err.response?.data?.msg || err.message));
      setSubmitting(false);
    }
  };

  // 提交考试
  const submitExam = () => {
    console.log('submitExam 函数被调用，直接提交答案');
    submitAnswers();
  };

  // 修改提交按钮处理函数
  const handleSubmitClick = () => {
    console.log('提交按钮被点击，调用紧急提交');
    // 直接调用紧急提交的逻辑
    console.log('紧急提交按钮被点击');
    submitAnswers();
  };

  // 倒计时
  useEffect(() => {
    if (timeLeft <= 0) {
      console.log('时间到，自动提交');
      // 直接内联调用而不是引用 submitExam 函数
      const answeredCount = Object.keys(answers).length;
      if (answeredCount > 0) {
        submitAnswers();
      } else {
        // 时间到，自动提交白卷
        console.log('时间到，自动提交白卷');
        submitAnswers();
      }
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, answers, submitAnswers]); // 修改依赖数组

  // 格式化时间
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // 处理答案变化
  const handleAnswerChange = (e) => {
    const updatedAnswers = { ...answers };
    updatedAnswers[questions[currentQuestion].id] = e.target.value;
    setAnswers(updatedAnswers);
  };

  // 下一题
  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  // 上一题
  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  // 跳转到指定题目
  const goToQuestion = (index) => {
    setCurrentQuestion(index);
  };

  // 如果试题数据尚未加载，显示加载中
  if (!questions || questions.length === 0) {
    return <div>加载中...</div>;
  }

  const question = questions[currentQuestion];
  const progress = Math.floor((Object.keys(answers).length / questions.length) * 100);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3}>{category} 考试 (35道选择题+15道判断题)</Title>
        <div>
          <Text strong style={{ fontSize: 18, color: timeLeft < 300 ? 'red' : 'inherit' }}>
            剩余时间: {formatTime(timeLeft)}
          </Text>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Progress percent={progress} status="active" />
        <Text>答题进度: {Object.keys(answers).length} / {questions.length} (每题2分，共100分，80分及格)</Text>
      </div>

      <Card title={`第 ${currentQuestion + 1} 题 (${question.category}) - ${question.type}`}>
        <div style={{ marginBottom: 16 }}>
          <Text strong>{question.content}</Text>
        </div>
        
        <Group onChange={handleAnswerChange} value={answers[question.id]}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {question.type === '选择题' ? (
              // 选择题显示ABCD选项
              question.options && question.options.map((option, index) => (
                <Radio key={index} value={String.fromCharCode(65 + index)}>
                  {String.fromCharCode(65 + index)}. {option}
                </Radio>
              ))
            ) : (
              // 判断题显示"正确"和"错误"选项
              <>
                <Radio value="正">正确</Radio>
                <Radio value="误">错误</Radio>
              </>
            )}
          </Space>
        </Group>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
        <Button onClick={prevQuestion} disabled={currentQuestion === 0}>
          上一题
        </Button>
        
        <Space>
          {/* 提交按钮 */}
          <Button 
            type="primary" 
            onClick={handleSubmitClick} 
            loading={submitting}
            danger={Object.keys(answers).length === 0}
          >
            {Object.keys(answers).length === 0 ? '交白卷' : '提交答卷'}
          </Button>
          
          <Button 
            type="primary" 
            onClick={nextQuestion} 
            disabled={currentQuestion === questions.length - 1}
          >
            下一题
          </Button>
        </Space>
      </div>

      <div style={{ marginTop: 24 }}>
        <Card title="题号导航" size="small">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {questions.map((_, index) => (
              <Button 
                key={index}
                type={answers[questions[index].id] ? 'primary' : 'default'}
                onClick={() => goToQuestion(index)}
                style={{ margin: 4 }}
              >
                {index + 1}
              </Button>
            ))}
          </div>
        </Card>
      </div>

      {/* 部分作答确认对话框 */}
      <Modal
        title="确认提交"
        open={confirmModalVisible}
        onOk={() => {
          console.log('确认对话框 - 确认提交');
          setConfirmModalVisible(false);
          submitAnswers();
        }}
        onCancel={() => {
          console.log('确认对话框 - 取消提交');
          setConfirmModalVisible(false);
        }}
        okText="确认提交"
        cancelText="取消"
      >
        <p>您还有 {questions ? questions.length - Object.keys(answers).length : 0} 题未作答，确定要提交吗？</p>
      </Modal>
      
      {/* 交白卷确认对话框 */}
      <Modal
        title="交白卷确认"
        open={whiteModalVisible}
        onOk={() => {
          console.log('白卷对话框 - 确认提交');
          setWhiteModalVisible(false);
          submitAnswers();
        }}
        onCancel={() => {
          console.log('白卷对话框 - 取消提交');
          setWhiteModalVisible(false);
        }}
        okText="确认交白卷"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <p>您还没有回答任何题目，确定要交白卷吗？这可能会导致不及格。</p>
      </Modal>
    </div>
  );
};

export default ExamPage; 